import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import Skeleton from "../Skeleton/Skeleton";
import { useConversation } from "hooks/useConversation";
import "./ConversationPanel.scss";
import NoMessages from "Assets/Img/NoMessages";
import TextArea from "../TextArea/TextArea";
import Button from "../Button/Button";

interface Props {
  projectId: string;
  projectName?: string;
  isActive?: boolean;
  preferConvName?: string;
  lender?: {
    companyName?: string;
  };
}

const MessageSkeleton = () => (
  <div className="convMsgSkeleton">
    <Skeleton width={28} height={28} borderRadius="50%" />
    <div className="convBubbleSkeleton">
      <Skeleton width={110} height={10} />
      <Skeleton width="72%" height={13} />
      <Skeleton width="50%" height={13} />
      <Skeleton width={70} height={9} />
    </div>
  </div>
);

const ConversationPanel: React.FC<Props> = ({
  projectId,
  projectName,
  isActive,
  preferConvName,
  lender,
}) => {
  const {
    conversations,
    activeId,
    messages,
    tabsLoading,
    msgsLoading,
    loadingMore,
    hasMore,
    sending,
    activeLabel,
    handleTabChange,
    handleSend,
    loadMoreMessages,
  } = useConversation({ projectId, isActive, preferConvName });

  const [input, setInput] = useState("");
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const threadRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);
  const prevScrollHeightRef = useRef(0);
  const prevScrollTopRef = useRef(0);
  const isPrependingRef = useRef(false);
  // Guards handleThreadScroll from reacting to programmatic scrollTop assignments
  const isRestoringScrollRef = useRef(false);
  // Tracks last message ID so useEffect only scrolls on genuinely new bottom messages
  const prevLastMsgIdRef = useRef<string | undefined>(undefined);

  const scrollToBottom = () => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  };

  const handleThreadScroll = () => {
    if (isRestoringScrollRef.current) return;
    const el = threadRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    isAtBottomRef.current = distFromBottom < 80;
    setShowScrollBtn(distFromBottom > 80);

    const scrollingUp = el.scrollTop < prevScrollTopRef.current;
    prevScrollTopRef.current = el.scrollTop;

    if (scrollingUp && el.scrollTop < 60 && hasMore && !loadingMore) {
      isPrependingRef.current = true; // set before async fetch
      prevScrollHeightRef.current = el.scrollHeight;
      loadMoreMessages().then((prepended) => {
        if (!prepended) isPrependingRef.current = false;
      });
    }
  };

  // After DOM updates: restore scroll position so the view doesn't jump on prepend
  useLayoutEffect(() => {
    const el = threadRef.current;
    if (isPrependingRef.current && el && prevScrollHeightRef.current > 0) {
      isRestoringScrollRef.current = true;
      // += delta keeps the user's exact view: offset from top stays the same
      // as the prepended content height added above them
      el.scrollTop += el.scrollHeight - prevScrollHeightRef.current;
      prevScrollTopRef.current = el.scrollTop;
      isPrependingRef.current = false;
      prevScrollHeightRef.current = 0;
      setTimeout(() => {
        isRestoringScrollRef.current = false;
      }, 0);
    }
  }, [messages]);

  // Scroll to bottom only when a genuinely new message appears at the end
  // (not on polling no-ops, not on prepend, not on tab-switch clears)
  useEffect(() => {
    const lastId = messages[messages.length - 1]?.id;
    const isNewAtBottom = lastId !== prevLastMsgIdRef.current;
    prevLastMsgIdRef.current = lastId;
    if (!isPrependingRef.current && isNewAtBottom && isAtBottomRef.current) {
      scrollToBottom();
    }
  }, [messages]);

  const cardTitle = projectName
    ? `MESSAGE THREAD — ${projectName.toUpperCase()}`
    : "MESSAGE THREAD";

  return (
    <div
      className={`section conversationPanel ${isActive ? "active" : ""}`}
      id="sec-messages"
    >
      <div className="convSectionHeader">
        <h1 className="sectionTitle">Messages</h1>
        <div className="convSectionSub">
          Communicate directly with CSC New England or{" "}
          {lender?.companyName || "the bank"}. All messages are logged to the
          project record.
        </div>
      </div>
      <div className="convCard">
        <div className="convCardTitle">{cardTitle}</div>
        <div className="convInner">
          <div className="convTabs">
            {tabsLoading ? (
              <div className="convTabSkeletons">
                <Skeleton width={70} height={28} borderRadius={20} />
                <Skeleton width={60} height={28} borderRadius={20} />
                <Skeleton width={70} height={28} borderRadius={20} />
              </div>
            ) : (
              conversations.map((c) => (
                <Button
                  key={c.id}
                  type="button"
                  className={`convTab ${activeId === c.id ? "active" : ""}`}
                  onClick={() => {
                    setShowScrollBtn(false);
                    handleTabChange(c.id);
                  }}
                >
                  {c.displayName}
                </Button>
              ))
            )}
          </div>

          <div
            className="convThread"
            ref={threadRef}
            onScroll={handleThreadScroll}
          >
            {loadingMore && (
              <div className="convLoadingMore">
                <span className="convLoadingDot" />
                <span className="convLoadingDot" />
                <span className="convLoadingDot" />
              </div>
            )}

            {msgsLoading || tabsLoading ? (
              <>
                <MessageSkeleton />
                <MessageSkeleton />
                <MessageSkeleton />
              </>
            ) : messages.length === 0 ? (
              <div className="convEmpty">
                <NoMessages />
                <p className="convEmptyTitle">No messages yet</p>
                <p className="convEmptyHint">
                  Start the conversation with {activeLabel || "your contact"}{" "}
                  below.
                </p>
              </div>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`convMsg ${msg.out ? "out" : ""} ${msg.pending ? "pending" : ""}`}
                >
                  <div className={`convAv ${msg.avatarVariant}`}>
                    {msg.initials}
                  </div>
                  <div className="convBubble">
                    <div className="convSender">{msg.sender}</div>
                    <div className="convText">{msg.text}</div>
                    <div className="convTime">{msg.time}</div>
                  </div>
                </div>
              ))
            )}
          </div>

          {showScrollBtn && (
            <Button
              type="button"
              className="convScrollBtn"
              onClick={scrollToBottom}
            >
              ↓ Jump to latest
            </Button>
          )}

          <div className="convCompose">
            <TextArea
              className={`convInput ${input.length > 5000 ? " convInputError" : ""}`}
              rows={2}
              placeholder={`Type a message to ${activeLabel}…`}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (input.length <= 5000)
                    handleSend(input, () => setInput(""));
                }
              }}
            />
            <Button
              type="button"
              className="convSend"
              onClick={() => handleSend(input, () => setInput(""))}
              disabled={sending || !input.trim() || input.length > 5000}
            >
              {sending ? "Sending…" : "Send"}
            </Button>
          </div>
          {input.length > 5000 && (
            <div className="convCharError">
              Message exceeds 5,000 character limit (
              {input.length.toLocaleString()} / 5,000)
            </div>
          )}
          <div className="convHint">
            Messages are visible to CSC New England and your lending officer at{" "}
            {lender?.companyName || "the bank"} where relevant.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversationPanel;
